import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useParams, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { BookingProvider } from "@/context/BookingContext";
import ScrollToTop from "@/components/ScrollToTop";
import ProtectedRoute from "@/components/ProtectedRoute";
import { CITIES, SERVICES, BRANDS } from "@/data/constants";

// Lazy loaded components
const Home = lazy(() => import("@/pages/Home"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const CityLanding = lazy(() => import("@/pages/CityLanding"));
const BrandLanding = lazy(() => import("@/pages/BrandLanding"));
const ModelLanding = lazy(() => import("@/pages/ModelLanding"));
const CityServiceLanding = lazy(() => import("@/pages/CityServiceLanding"));
const MasterServiceLanding = lazy(() => import("@/pages/MasterServiceLanding"));
const CityModelServiceLanding = lazy(() => import("@/pages/CityModelServiceLanding"));
const SelectModel = lazy(() => import("@/pages/SelectModel"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const WarrantyPolicy = lazy(() => import("@/pages/WarrantyPolicy"));
const RefundPolicy = lazy(() => import("@/pages/RefundPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const BookConsultation = lazy(() => import("@/pages/BookConsultation"));
const BookingForm = lazy(() => import("@/components/BookingForm").then(m => ({ default: m.BookingForm })));

const CITY_SLUGS = CITIES.map((c) => c.slug);
const SERVICE_SLUGS = SERVICES.map((s) => s.slug);
const BRAND_SLUGS = BRANDS.map((b) => b.slug);

const SERVICE_SUFFIX_RE = new RegExp(`-(${SERVICE_SLUGS.join("|")})$`);

// Detect whether a slug is brand-model-service-city (has 4 parts) vs brand-service-city (3 parts).
// Strategy: if ends with city AND the remainder (without city) ends with service AND the remainder
// without that service starts with "{brand}-" followed by something non-empty -> it's model-service-city
function isCityModelService(slug) {
  const cityMatch = CITY_SLUGS.find((c) => slug.endsWith(`-${c}`));
  if (!cityMatch) return false;
  const noCity = slug.slice(0, -(cityMatch.length + 1));
  const serviceMatch = SERVICE_SLUGS.find((s) => noCity.endsWith(`-${s}`));
  if (!serviceMatch) return false;
  const noService = noCity.slice(0, -(serviceMatch.length + 1));
  // noService should be of form "{brandSlug}-{modelSlug}" where modelSlug is non-empty
  const brand = BRAND_SLUGS.find((b) => noService.startsWith(`${b}-`));
  if (!brand) return false;
  const modelSlug = noService.slice(brand.length + 1);
  return modelSlug.length > 0;
}

function isCityService(slug) {
  const cityMatch = CITY_SLUGS.find((c) => slug.endsWith(`-${c}`));
  if (!cityMatch) return false;
  const noCity = slug.slice(0, -(cityMatch.length + 1));
  const serviceMatch = SERVICE_SLUGS.find((s) => noCity.endsWith(`-${s}`));
  if (!serviceMatch) return false;
  // noCity without service should be exactly a brand slug
  const noService = noCity.slice(0, -(serviceMatch.length + 1));
  return BRAND_SLUGS.includes(noService);
}

function CityLandingOrRedirect() {
  const { citySlug } = useParams();
  if (citySlug && citySlug.startsWith("doorstep-mobile-repair-")) {
    return <CityLanding />;
  }
  return <Navigate to={`/repair/doorstep-mobile-repair-${citySlug}`} replace />;
}

function DynamicSlugRouter() {
  const { slug } = useParams();
  if (!slug) return <Navigate to="/" replace />;

  // 1. Brand × Model × Service × City: /apple-iphone-15-pro-screen-replacement-delhi
  if (isCityModelService(slug)) return <CityModelServiceLanding />;

  // 2. Brand × Service × City: /apple-screen-replacement-delhi
  if (isCityService(slug)) {
    return <CityServiceLanding />;
  }

  // 3. Brand × Model × Service: /apple-iphone-15-pro-screen-replacement
  if (SERVICE_SUFFIX_RE.test(slug)) return <ModelLanding />;

  // 4. Brand hub: /apple-repair
  if (/-repair$/.test(slug)) return <BrandLanding />;

  return <Navigate to="/" replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <BookingProvider>
          <Suspense fallback={<div className="h-screen w-screen bg-brand-black flex items-center justify-center"><div className="h-8 w-8 border-2 border-brand-orange border-t-transparent rounded-full animate-spin"></div></div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/repair/:citySlug" element={<CityLandingOrRedirect />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/service/:slug" element={<MasterServiceLanding />} />
              <Route path="/select-model" element={<SelectModel />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/warranty-policy" element={<WarrantyPolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/book-consultation" element={<BookConsultation />} />
              {/* Alternate booking URLs — all redirect to canonical /book-consultation */}
              <Route path="/book/consultation" element={<Navigate to="/book-consultation" replace />} />
              <Route path="/book/appointment" element={<Navigate to="/book-consultation" replace />} />
              <Route path="/book-appointment" element={<Navigate to="/book-consultation" replace />} />
              <Route path="/:slug" element={<DynamicSlugRouter />} />
            </Routes>
          </Suspense>
          <Suspense fallback={null}>
            <BookingForm />
          </Suspense>
          <Toaster
            theme="dark"
            position="top-center"
            toastOptions={{
              style: {
                background: "#141415",
                color: "#fff",
                border: "1px solid #27272A",
                borderRadius: "2px",
              },
            }}
          />
        </BookingProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
